import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';

const CONTACT_OPTIONS = [
  { icon: '🚀', label: 'Start a free trial', desc: 'Get started with DepGuard in under 5 minutes. No credit card required.', cta: 'Start free', to: '/trial?plan=professional', accent: '#6366f1' },
  { icon: '📅', label: 'Book a demo', desc: 'See DepGuard analyse a real SBOM live. 30 minutes, no slides.', cta: 'Book demo', to: '/contact?type=demo', accent: '#8b5cf6' },
  { icon: '🏢', label: 'Enterprise sales', desc: 'On-premises deployment, custom policy packs, SLA, and dedicated support.', cta: 'Talk to sales', to: '/contact?type=enterprise', accent: '#06b6d4' },
];

const SUPPORT_ITEMS = [
  { q: 'Technical support', a: 'Open a support ticket via the platform portal or email support@lexint.io', icon: '🔧' },
  { q: 'Security disclosure', a: 'Report vulnerabilities to security@lexint.io. We follow responsible disclosure.', icon: '🔐' },
  { q: 'Partnership enquiries', a: 'Interested in integrating or reselling? Contact partners@lexint.io', icon: '🤝' },
  { q: 'Press & analysts', a: 'For media enquiries and analyst briefings contact press@lexint.io', icon: '📰' },
  { q: 'Phone', a: 'Call us directly at +31 6 54 22 06 45 during business hours (CET).', icon: '📞' },
];

export default function WebsiteContactPage() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  return (
    <div className="dgw-page-wrap">

      {/* Hero */}
      <section className="dgw-hero" style={{textAlign:'center'}}>
        <div className="dgw-hero-inner" style={{maxWidth:760}}>
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            Get in touch
          </div>
          <h1>Tell us what you need.</h1>
          <p className="dgw-hero-lead" style={{maxWidth:560, margin:'0 auto 40px'}}>
            Whether you need a demo, want to evaluate DepGuard, or have questions about
            enterprise deployment. We respond within one business day.
          </p>
        </div>
      </section>

      {/* Contact options */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1000, margin:'0 auto'}}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24}}>
            {CONTACT_OPTIONS.map(opt => (
              <div key={opt.label} style={{
                padding:'32px', borderRadius:16,
                border:`1px solid ${opt.accent}33`,
                background:`${opt.accent}06`,
                display:'flex', flexDirection:'column', gap:12,
              }}>
                <div style={{fontSize:32}}>{opt.icon}</div>
                <h3 style={{fontSize:18, fontWeight:700, color:'#0f172a', margin:0}}>{opt.label}</h3>
                <p style={{fontSize:14, color:'#475569', lineHeight:1.6, margin:0, flexGrow:1}}>{opt.desc}</p>
                <Link to={opt.to} className="dgw-btn dgw-btn-primary" style={{
                  background:opt.accent, marginTop:8, display:'inline-flex',
                  justifyContent:'center',
                }}>{opt.cta} →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick message form */}
      <section className="dgw-section" style={{background:'#f8fafc'}}>
        <div style={{maxWidth:640, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag">Send a message</span>
            <h2>Or just drop us a note.</h2>
            <p className="dgw-section-lead">Tell us what you need. We reply within one business day.</p>
          </div>

          {sent ? (
            <div style={{
              padding:40, borderRadius:16, background:'rgba(16,185,129,.08)',
              border:'1px solid rgba(16,185,129,.2)', textAlign:'center',
            }}>
              <div style={{fontSize:48, marginBottom:16}}>✅</div>
              <h3 style={{color:'#0f172a', marginBottom:8}}>Message received!</h3>
              <p style={{color:'#475569'}}>We'll get back to you within one business day.</p>
            </div>
          ) : (
            <form onSubmit={async e => {
              e.preventDefault();
              setError('');
              setLoading(true);
              const data = new FormData(e.target);
              try {
                await axios.post(`${config.BASE_URL}/contact`, {
                  name: data.get('name') || '',
                  email: data.get('email') || '',
                  company: data.get('company') || '',
                  message: data.get('message') || '',
                  contactType: 'general',
                });
                setSent(true);
              } catch (err) {
                setError(err.response?.data?.error || 'Something went wrong. Please try again.');
              } finally {
                setLoading(false);
              }
            }}
              style={{display:'flex', flexDirection:'column', gap:16}}>
              <div className="dgw-form-row">
                {[['Name', 'name'], ['Company', 'company']].map(([label, fieldName]) => (
                  <div key={label} style={{display:'flex', flexDirection:'column', gap:6}}>
                    <label style={{fontSize:13, fontWeight:600, color:'#374151'}}>{label}</label>
                    <input required name={fieldName} placeholder={label} style={{
                      padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0',
                      fontSize:14, color:'#0f172a', outline:'none',
                      background:'#fff', fontFamily:'inherit',
                    }}/>
                  </div>
                ))}
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:6}}>
                <label style={{fontSize:13, fontWeight:600, color:'#374151'}}>Work email</label>
                <input required type="email" name="email" placeholder="you@company.com" style={{
                  padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0',
                  fontSize:14, color:'#0f172a', outline:'none',
                  background:'#fff', fontFamily:'inherit',
                }}/>
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:6}}>
                <label style={{fontSize:13, fontWeight:600, color:'#374151'}}>What can we help with?</label>
                <textarea required name="message" rows={4} placeholder="Tell us about your use case, team size, or questions..." style={{
                  padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0',
                  fontSize:14, color:'#0f172a', outline:'none', resize:'vertical',
                  background:'#fff', fontFamily:'inherit',
                }}/>
              </div>
              {error && <div style={{color:'#ef4444', fontSize:14, padding:'8px 0'}}>{error}</div>}
              <button type="submit" className="dgw-btn dgw-btn-primary" style={{alignSelf:'flex-start'}} disabled={loading}>
                {loading ? 'Sending…' : 'Send message →'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Support channels */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:860, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:32}}>
            <span className="dgw-section-tag">Other channels</span>
            <h2>Specific enquiries.</h2>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:16}}>
            {SUPPORT_ITEMS.map(s => (
              <div key={s.q} style={{
                padding:'20px', borderRadius:12,
                background:'#f8fafc', border:'1px solid #e2e8f0',
              }}>
                <div style={{fontSize:24, marginBottom:8}}>{s.icon}</div>
                <div style={{fontSize:13, fontWeight:700, color:'#0f172a', marginBottom:6}}>{s.q}</div>
                <div style={{fontSize:13, color:'#64748b', lineHeight:1.5}}>{s.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
